import { useEffect, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Anchor, Loader, Text } from '@mantine/core'
import InviteAuthShell from '@/PageComponents/Auth/InviteAuthShell'
import WrongAccountScreen from '@/PageComponents/Auth/WrongAccountScreen'
import { PlatformAuthService, hydrateTenantSession } from '@/services/platform-auth'
import { claimToStringArray, decodeJwtPayload } from '@/utils/jwt'
import { login, clearCookie } from '@/utils/auth'
import Storage from '@/utils/storage'
import config from '@/utils/config'
import * as Enums from '@/utils/enums'

function InviteConsumePage() {
  const { token } = Route.useParams()
  const [peek, setPeek] = useState<{ valid: boolean; expired: boolean; email?: string; companyName?: string; firstName?: string | null; lastName?: string | null } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const autoConsumedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const service = new PlatformAuthService()
        const result = await service.peekMagicLink(token)
        if (!cancelled) setPeek(result)
      } catch {
        if (!cancelled) setPeek({ valid: false, expired: true })
      }
    }
    load()
    return () => { cancelled = true }
  }, [token])

  useEffect(() => {
    if (!peek || !peek.valid || peek.expired) return
    const invitedEmail = (peek.email || '').trim()
    const signedInEmail = (Storage.getCookie(Enums.Cookie.servUserName) || '').trim()
    const hasSession = !!Storage.getCookie(Enums.Cookie.token) && !!signedInEmail
    const isWrongAccount = hasSession && !!invitedEmail && signedInEmail.toLowerCase() !== invitedEmail.toLowerCase()
    if (isWrongAccount || autoConsumedRef.current) return
    autoConsumedRef.current = true
    continueInvite()
  }, [peek])

  const persistSession = async (payload: any) => {
    if (payload?.refreshToken) {
      Storage.setCookie('platformRefreshToken', payload.refreshToken, 7)
    }
    Storage.setCookie('platformAccessToken', payload.accessToken, 7)

    const claims = decodeJwtPayload(payload.accessToken) ?? {}
    const selectedTenant = payload.availableTenants?.[0]
    let legacyApiHost = payload.apiUrl || claims.api_url || ''
    if (config.isDebugging()) {
      legacyApiHost = config.apiHost
    }
    const hydratedData = await hydrateTenantSession(legacyApiHost, payload.accessToken)
    const appRoles = claimToStringArray(claims.app_roles)
    const subInfo = hydratedData?.subscriptionInfo || selectedTenant?.subscriptionInfo
    const subscriptionInfo = subInfo
      ? {
          AccessStatus: subInfo.AccessStatus ?? subInfo.accessStatus,
          CustomerStatus: subInfo.CustomerStatus ?? subInfo.customerStatus,
          AccessEndDate: subInfo.AccessEndDate ?? subInfo.accessEndDate,
          UserCount: subInfo.UserCount ?? subInfo.userCount,
          Message: subInfo.Message,
          MessageOwner: subInfo.MessageOwner,
          MessageNonOwner: subInfo.MessageNonOwner,
          SignUpCompleteDate: subInfo.SignUpCompleteDate,
          MultiStore: subInfo.MultiStore ?? subInfo.multiStore
        }
      : {
          AccessStatus: Enums.AccessStatus.Live,
          CustomerStatus: 'Active',
          AccessEndDate: null,
          UserCount: 1
        }

    const companyName = selectedTenant?.tradingName || peek?.companyName || claims.tenant_code || ''
    sessionStorage.setItem('sc:completeInvite', JSON.stringify({
      requiresPassword: !!payload.requiresPassword,
      requiresProfile: !!payload.requiresProfile,
      email: peek?.email || claims.email || '',
      firstName: payload.firstName || peek?.firstName || '',
      lastName: payload.lastName || peek?.lastName || '',
      companyName
    }))

    login({
      token: payload.accessToken,
      tenantID: claims.tenant_id || selectedTenant?.tenantID || '',
      userID: claims.sub || '',
      employeeID: hydratedData?.employeeID || '',
      duration: 7,
      userName: claims.email || peek?.email || '',
      fullName: hydratedData?.fullName || claims.full_name || claims.email || '',
      companyName,
      permissions: claimToStringArray(claims['https://servcraft.com/permissions']),
      isOwner: appRoles.includes('Owner'),
      subscriptionInfo,
      apiHost: legacyApiHost,
      fingerPrint: '',
      pageSize: hydratedData?.pageSize || 20,
      skipLegacyIntegrations: true
    })
  }

  const continueInvite = async () => {
    setBusy(true)
    setError('')
    try {
      const service = new PlatformAuthService()
      const payload = await service.consumeMagicLink(token)
      await persistSession(payload)
    } catch (err: any) {
      setPeek({ valid: false, expired: true, companyName: peek?.companyName })
      setError(err?.message || 'This invite link has expired or already been used.')
      setBusy(false)
    }
  }

  if (!peek) {
    return (
      <InviteAuthShell>
        <Loader size="sm" />
        <Text mt="md" c="#6B6F76">Checking your invite…</Text>
      </InviteAuthShell>
    )
  }

  if (!peek.valid || peek.expired) {
    return (
      <InviteAuthShell>
        <ExpiredIcon />
        <Text size="28px" fw={700} c="#101828" mb={8}>This invite link no longer works</Text>
        <Text size="md" c="#4b5563" mb={20}>
          It may have expired, been used already or been cancelled. Ask your admin{peek.companyName ? ` at ${peek.companyName}` : ''} to send you a new one.
        </Text>
        <Text ta="center">
          <Anchor href="/login" size="sm" fw={600} c="scBlue">I already have an account — sign in</Anchor>
        </Text>
        {error && <Text size="sm" c="red" mt="sm">{error}</Text>}
      </InviteAuthShell>
    )
  }

  const company = peek.companyName || 'ServCraft'
  const invitedEmail = (peek.email || '').trim()
  const signedInEmail = (Storage.getCookie(Enums.Cookie.servUserName) || '').trim()
  const hasSession = !!Storage.getCookie(Enums.Cookie.token) && !!signedInEmail
  const isWrongAccount = hasSession && !!invitedEmail && signedInEmail.toLowerCase() !== invitedEmail.toLowerCase()

  const signOutAndContinue = () => {
    clearCookie(true)
    continueInvite()
  }

  if (isWrongAccount) {
    return (
      <InviteAuthShell>
        <WrongAccountScreen
          company={company}
          invitedEmail={invitedEmail}
          signedInEmail={signedInEmail}
          busy={busy}
          error={error}
          onSignOut={signOutAndContinue}
          onCancel={() => { window.location.href = '/' }}
        />
      </InviteAuthShell>
    )
  }

  return (
    <InviteAuthShell>
      <Loader size="sm" />
      <Text mt="md" c="#6B6F76">Signing you in…</Text>
    </InviteAuthShell>
  )
}

function ExpiredIcon() {
  return (
    <div style={{
      width: 48, height: 48, borderRadius: 24, background: '#fef3c7', color: '#b45309',
      display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16
    }}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    </div>
  )
}

export const Route = createFileRoute('/i/$token')({ component: InviteConsumePage })
export default InviteConsumePage
